import type { CSSProperties } from 'react';
import type { RewardBadge } from '@/lib/api/rewards-hub/models';
import { cn } from '@/lib/ui-kit/utils';
import {
  achievementGlyphFor,
  BADGE_GLYPH,
  badgeFrameForBadge,
  badgeWellFor,
  SEASON_GROUP,
} from './badgeFrames';

/**
 * The achievement medallion, on its own so anything that needs one — the
 * achievements card, a quest dialog, the leaderboard's rank tooltip — can draw
 * it without pulling in the card it was first written for.
 */

interface BadgeMedallionProps {
  /**
   * The frame is the step (`tierIndex`, or the derived tier badge's code); the
   * glyph is the backend's `image` (uploaded in the admin panel), with the
   * glyph drawn here per achievement code as the fallback (29.09).
   */
  badge: Pick<RewardBadge, 'image' | 'emoji' | 'title' | 'owned'> & {
    tierIndex?: number | null;
    group?: string | null;
    rarity?: string;
    code?: string | null;
  };
  size?: number;
  className?: string;
}

interface MedallionGlyphProps {
  badge: BadgeMedallionProps['badge'];
}

function MedallionGlyph({ badge }: MedallionGlyphProps) {
  // A season badge's art is its `im` alone: one still being drawn keeps the
  // well empty, as the first cell of each row in the design does.
  if (badge.group === SEASON_GROUP && !badge.image) return null;
  // The admin panel's image first; the built-in glyphs only stand in for a
  // badge uploaded without one (29.09).
  const glyph = badge.image ?? achievementGlyphFor(badge.code);
  if (!glyph && badge.emoji) {
    return <span className="text-[calc(var(--badge-size)*0.3)] leading-none">{badge.emoji}</span>;
  }
  return (
    <img
      loading="lazy"
      decoding="async"
      src={glyph ?? BADGE_GLYPH}
      alt=""
      className="size-full object-contain"
      draggable={false}
    />
  );
}

/**
 * Winged frame + dark well + the badge glyph. Inner measures are the Figma
 * ratios of the 72px medallion written as percentages of the frame, so the
 * whole thing follows its box — `size` sets that box through `--badge-size`,
 * and a caller can override it per breakpoint from `className`
 * (`max-sm:size-[52px]`) without the inner parts drifting out of proportion.
 */
export function BadgeMedallion({ badge, size = 72, className }: BadgeMedallionProps) {
  return (
    <span
      className={cn(
        'relative inline-block size-(--badge-size) shrink-0',
        !badge.owned && 'opacity-40 grayscale',
        className,
      )}
      style={{ '--badge-size': `${size}px` } as CSSProperties}
      title={badge.title}
    >
      <img
        loading="lazy"
        decoding="async"
        src={badgeFrameForBadge(badge)}
        alt=""
        className="absolute inset-0 size-full object-contain"
        draggable={false}
      />
      <span
        aria-hidden
        className="absolute top-[49%] left-[50.5%] size-[45.45%] -translate-x-1/2 -translate-y-1/2 rounded-full"
        style={{ backgroundImage: badgeWellFor(badge.group) }}
      />
      <span
        className="absolute top-[47.7%] left-1/2 flex size-[33.33%] -translate-x-1/2 -translate-y-1/2
          items-center justify-center"
      >
        <MedallionGlyph badge={badge} />
      </span>
    </span>
  );
}
